import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export default function HowItWorks() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const steps = [
    {
      number: '01',
      icon: 'ri-map-pin-add-line',
      title: t('homeSections.howItWorks.steps.1.title'),
      description: t('homeSections.howItWorks.steps.1.description'),
    },
    {
      number: '02',
      icon: 'ri-settings-4-line',
      title: t('homeSections.howItWorks.steps.2.title'),
      description: t('homeSections.howItWorks.steps.2.description'),
    },
    {
      number: '03',
      icon: 'ri-sparkling-2-line',
      title: t('homeSections.howItWorks.steps.3.title'),
      description: t('homeSections.howItWorks.steps.3.description'),
    },
    {
      number: '04',
      icon: 'ri-user-star-line',
      title: t('homeSections.howItWorks.steps.4.title'),
      description: t('homeSections.howItWorks.steps.4.description'),
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.15 }
    );

    cardRefs.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="como-funciona" className="py-28 px-6 bg-[#0A1628] relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 rounded-full bg-[#C9A84C]/5 blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-[#C9A84C]/5 blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#C9A84C]/40 bg-[#C9A84C]/10 mb-6">
            <span className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest">{t('homeSections.howItWorks.badge')}</span>
          </div>
          <h2 className="text-5xl font-extrabold text-white mb-5 leading-tight">
            {t('homeSections.howItWorks.titleBefore')} <span className="text-[#C9A84C]">{t('homeSections.howItWorks.titleHighlight')}</span> {t('homeSections.howItWorks.titleAfter')}
          </h2>
          <div className="w-20 h-0.5 bg-[#C9A84C] mx-auto mb-6"></div>
          <p className="text-white/90 text-lg max-w-2xl mx-auto leading-relaxed">
            {t('homeSections.howItWorks.description')}
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {/* Connector line (desktop only) */}
          <div className="hidden lg:block absolute top-16 left-[12.5%] right-[12.5%] h-px border-t-2 border-dashed border-[#C9A84C]/30 z-0"></div>

          {steps.map((step, index) => (
            <div
              key={index}
              ref={(el) => { cardRefs.current[index] = el; }}
              className="opacity-0 translate-y-8 transition-all duration-700 relative z-10"
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className="group bg-white/5 border border-white/10 rounded-2xl p-8 text-center hover:bg-white/10 hover:border-[#C9A84C]/40 hover:shadow-[0_0_40px_rgba(201,168,76,0.1)] transition-all duration-500 h-full flex flex-col items-center">
                {/* Number circle */}
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gradient-to-br from-[#C9A84C] to-[#E8D4A0] mb-5 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <span className="text-[#0A1628] font-extrabold text-xl">{step.number}</span>
                </div>

                {/* Icon */}
                <div className="w-14 h-14 flex items-center justify-center mb-5">
                  <i className={`${step.icon} text-4xl text-[#C9A84C]`}></i>
                </div>

                {/* Title */}
                <h3 className="text-white font-bold text-lg mb-3 leading-snug">{step.title}</h3>

                {/* Description */}
                <p className="text-white/90 text-sm leading-relaxed">{step.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <button
            onClick={scrollToContact}
            className="inline-flex items-center gap-3 px-10 py-4 bg-[#C9A84C] text-[#0A1628] font-bold text-base rounded-xl hover:bg-[#B89840] transition-all duration-300 shadow-xl hover:shadow-[#C9A84C]/40 hover:scale-105 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-sparkling-2-line text-lg"></i>
            {t('homeSections.howItWorks.cta')}
          </button>
        </div>
      </div>
    </section>
  );
}
